"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { FileText, MoreHorizontal, Copy, Link2, Trash2, Download, Settings, ChevronRight, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LinkSettingsDialog } from "@/components/link-settings-dialog"
import { copyToClipboard } from '@/utils/clipboard'
import { formatTimeAgo, formatFileSize } from '@/api/history'
import type { TransferHistoryItem, TransferHistoryFile } from '@/api/history'

interface Transfer {
  id: string
  name: string
  downloaded: number
  uploaded: number
  transferred: number
  uploadTime: string
  createdAt: string // 添加原始创建时间用于排序
  status: string
  statusType: "active" | "failed"
  shareLink?: string
  shareKey?: string
  files: TransferHistoryFile[]
  fileCount: number
  description?: string       // 备注信息
  encrypted?: boolean        // 是否加密传输
  allowDownload?: boolean    // 允许下载
  allowClone?: boolean       // 允许克隆
  allowTransfer?: boolean    // 允许转存
  burnAfterRead?: boolean    // 下载后销毁
  maxDownloads?: number      // 最大下载次数
  expiresAt?: string         // 过期时间
}

interface TransferHistoryListProps {
  searchQuery: string
  sortOrder: "asc" | "desc"
  selectedIds: string[]
  onSelectChange: (ids: string[]) => void
  transfers: TransferHistoryItem[]
  onDelete: (id: string) => void
  onRevoke: (id: string) => void
}

export function TransferHistoryList({
  searchQuery,
  sortOrder,
  selectedIds,
  onSelectChange,
  transfers: historyItems,
  onDelete,
  onRevoke: _onRevoke, // 暂时未使用（撤销功能已注释）
}: TransferHistoryListProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null)
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({})

  // 转换历史数据为UI需要的格式（使用 useMemo 避免重复计算）
  const transfers: Transfer[] = useMemo(() => historyItems.map(item => {
    // 获取第一个文件名作为显示名称（优先 displayName）
    const fileName = item.files.length > 0 
      ? (item.files[0].displayName || item.files[0].fileName) 
      : (item.shareName || '未命名分享')
    
    // 计算状态文本
    let status = ''
    let statusType: "active" | "failed" = "failed"
    
    if (item.status === 'revoked') {
      status = '已撤销'
    } else if (item.status === 'burned') {
      status = '已销毁'
    } else if (item.isExpired) {
      status = '已过期'
    } else if (item.isActive) {
      // 计算距离过期时间
      const now = new Date()
      const expires = new Date(item.expiresAt)
      const hoursLeft = Math.floor((expires.getTime() - now.getTime()) / (1000 * 60 * 60))
      const daysLeft = Math.floor(hoursLeft / 24)
      
      if (daysLeft > 0) {
        status = `${daysLeft}天后到期`
      } else if (hoursLeft > 0) {
        status = `${hoursLeft}小时后到期`
      } else {
        status = '即将过期'
      }
      statusType = 'active'
    } else {
      status = '失效传输'
    }
    
    return {
      id: item.id,
      name: fileName,
      downloaded: item.downloadCount,
      uploaded: 0, // API暂未提供
      transferred: item.fileCount,
      uploadTime: formatTimeAgo(item.createdAt),
      createdAt: item.createdAt, // 保存原始创建时间用于排序
      status,
      statusType,
      shareLink: item.downloadUrl ? `${window.location.origin}${item.downloadUrl}` : undefined,
      shareKey: item.shareKey,
      files: item.files,
      fileCount: item.fileCount,
      description: item.description,
      encrypted: item.encrypted,
      allowDownload: item.allowDownload,
      allowClone: item.allowClone,
      allowTransfer: item.allowTransfer,
      burnAfterRead: item.burnAfterRead,
      maxDownloads: item.maxDownloads,
      expiresAt: item.expiresAt,
    }
  }), [historyItems])

  // 高亮文本函数：将匹配的文本高亮显示（支持多个匹配）
  const highlightText = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) {
      return text
    }
    
    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let index = lowerText.indexOf(lowerQuery, lastIndex)
    
    while (index !== -1) {
      // 添加匹配前的文本
      if (index > lastIndex) {
        parts.push(text.substring(lastIndex, index))
      }
      
      // 添加高亮的匹配文本
      parts.push(
        <mark key={index} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">
          {text.substring(index, index + query.length)}
        </mark>
      )
      
      lastIndex = index + query.length
      index = lowerText.indexOf(lowerQuery, lastIndex)
    }
    
    // 添加剩余的文本
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }
    
    return parts.length > 0 ? <>{parts}</> : text
  }

  // 检查文件名是否匹配搜索条件
  const fileMatchesQuery = (file: TransferHistoryFile, query: string): boolean => {
    if (!query.trim()) return true
    const fileName = (file.displayName || file.fileName).toLowerCase()
    return fileName.includes(query.toLowerCase())
  }

  // 检查批次是否匹配搜索条件（批次名称或任何文件名匹配）
  const transferMatchesQuery = (transfer: Transfer, query: string): boolean => {
    if (!query.trim()) return true
    const lowerQuery = query.toLowerCase()
    
    // 检查批次名称
    if (transfer.name.toLowerCase().includes(lowerQuery)) {
      return true
    }
    
    // 检查是否有任何文件名匹配
    return transfer.files.some(file => fileMatchesQuery(file, query))
  }

  // 过滤和排序（使用 useMemo 避免重复计算）
  const filteredTransfers = useMemo(() => {
    return transfers
      .filter((t) => transferMatchesQuery(t, searchQuery))
      .sort((a, b) => {
        // 基于实际创建时间排序，而不是格式化后的字符串
        const timeA = new Date(a.createdAt).getTime()
        const timeB = new Date(b.createdAt).getTime()
        if (sortOrder === "asc") {
          return timeA - timeB // 升序：早的在前
        }
        return timeB - timeA // 降序：晚的在前（默认）
      })
  }, [transfers, searchQuery, sortOrder])

  // 如果有搜索条件，自动展开包含匹配文件的批次
  useEffect(() => {
    if (searchQuery.trim()) {
      const newExpandedIds: Record<string, boolean> = {}
      const lowerQuery = searchQuery.toLowerCase()
      // 直接使用 transfers 而不是 filteredTransfers，避免循环依赖
      transfers.forEach(transfer => {
        // 如果批次中有匹配的文件，自动展开
        const hasMatchedFile = transfer.files.some(file => {
          const fileName = (file.displayName || file.fileName).toLowerCase()
          return fileName.includes(lowerQuery)
        })
        if (hasMatchedFile) {
          newExpandedIds[transfer.id] = true
        }
      })
      setExpandedIds(prev => {
        // 检查是否有变化，避免不必要的更新
        const hasChanges = Object.keys(newExpandedIds).some(
          id => !prev[id] || prev[id] !== newExpandedIds[id]
        )
        if (!hasChanges && Object.keys(newExpandedIds).length === 0 && Object.keys(prev).length === 0) {
          return prev // 没有变化，返回原对象
        }
        return { ...prev, ...newExpandedIds }
      })
    }
  }, [searchQuery, transfers])

  const handleSelect = useCallback((id: string, checked: boolean) => {
    if (checked) {
      onSelectChange([...selectedIds, id])
    } else {
      onSelectChange(selectedIds.filter((i) => i !== id))
    }
  }, [selectedIds, onSelectChange])

  const handleCopyLink = async (transfer: Transfer) => {
    if (!transfer.shareLink) return
    
    const code = transfer.shareKey
    const text = code 
      ? `${transfer.shareLink}\n口令: ${code}` 
      : transfer.shareLink
    
    const success = await copyToClipboard(text)
    if (success) {
      setCopiedId(transfer.id)
      setTimeout(() => setCopiedId(null), 2000)
    }
  }

  const handleOpenSettings = (transfer: Transfer) => {
    setSelectedTransfer(transfer)
    setSettingsDialogOpen(true)
  }

  const handleDownload = (transfer: Transfer) => {
    // 下载逻辑 - 打开下载页面
    if (transfer.shareKey) {
      window.open(`/download/group/${transfer.shareKey}`, '_blank')
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const handleDeleteClick = (transfer: Transfer) => {
    if (confirm(`确定要删除"${transfer.name}"吗？`)) {
      onDelete(transfer.id)
    }
  }

  return (
    <div className="divide-y divide-gray-100">
      {filteredTransfers.map((transfer) => (
        <div
          key={transfer.id}
          className={`group grid grid-cols-[auto_1fr_200px_120px] gap-4 px-6 py-4 hover:bg-gray-50 transition-colors ${
            selectedIds.includes(transfer.id) ? "bg-blue-50/30" : ""
          }`}
        >
          {/* 复选框 */}
          <div className="flex items-center">
            <Checkbox
              checked={selectedIds.includes(transfer.id)}
              onCheckedChange={(checked) => handleSelect(transfer.id, checked === true)}
            />
          </div>

          {/* 文件信息 */}
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="w-8 h-8 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => toggleExpand(transfer.id)}
                className="inline-flex items-center gap-2 hover:opacity-90"
                title="展开/收起文件"
              >
                {expandedIds[transfer.id] ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
                <span 
                  className="text-sm font-medium text-gray-900 truncate max-w-[32rem]"
                  title={transfer.name}
                >
                  {highlightText(transfer.name, searchQuery)}
                </span>
                <span className="text-gray-600">等</span>
                <span className="text-blue-600 font-bold text-base">{transfer.fileCount}</span>
                <span className="text-gray-600">个文件</span>
              </button>
              <p className="text-xs text-gray-500 mt-1">
                下载 {transfer.downloaded} 消耗 {transfer.uploaded} 转存 {transfer.transferred} 项 上传 {transfer.uploadTime}
              </p>
              {expandedIds[transfer.id] && (
                <div className="mt-2 space-y-1">
                  {transfer.files.map((file: TransferHistoryFile, idx: number) => {
                    const fileName = file.displayName || file.fileName
                    const isMatched = fileMatchesQuery(file, searchQuery)
                    return (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-2 text-xs ${
                          isMatched ? 'text-gray-900 font-medium' : 'text-gray-600'
                        }`}
                      >
                        <FileText className={`w-3.5 h-3.5 ${isMatched ? 'text-yellow-600' : ''}`} />
                        <span 
                          className="truncate"
                          title={fileName}
                        >
                          {highlightText(fileName, searchQuery)}
                        </span>
                        <span className="text-gray-400">({formatFileSize(file.fileSize)})</span>
                      </div>
                    )
                  })}
                  {transfer.fileCount > transfer.files.length && (
                    <div className="text-xs text-gray-500">
                      ...还有 {transfer.fileCount - transfer.files.length} 个文件
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 状态 */}
          <div className="flex items-center">
            <span
              className={`text-sm px-3 py-1.5 rounded-lg ${
                transfer.statusType === "active" ? "text-gray-900 font-medium" : "text-gray-500"
              }`}
            >
              {transfer.status}
            </span>
          </div>

          {/* 操作 */}
          <div className="flex items-center justify-end gap-2">
            {transfer.shareLink && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-400 hover:text-gray-900"
                onClick={() => handleCopyLink(transfer)}
                title="复制链接"
              >
                {copiedId === transfer.id ? (
                  <Copy className="w-4 h-4 text-green-600" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
              </Button>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-900">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                {transfer.shareLink && (
                  <DropdownMenuItem onClick={() => handleOpenSettings(transfer)}>
                    <Settings className="w-4 h-4 mr-2" />
                    链接设置
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => handleDownload(transfer)}>
                  <Download className="w-4 h-4 mr-2" />
                  下载
                </DropdownMenuItem>
                {/* 撤销功能暂时注释掉 */}
                {/* {transfer.statusType === "active" && (
                  <DropdownMenuItem
                    onClick={() => {
                      if (confirm(`确定要撤销该分享吗？撤销后他人将无法下载。`)) {
                        onRevoke(transfer.id)
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    撤销
                  </DropdownMenuItem>
                )} */}
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => handleDeleteClick(transfer)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  删除
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ))}
      
      {/* 链接设置对话框 */}
      {selectedTransfer && (
        <LinkSettingsDialog
          open={settingsDialogOpen}
          onClose={() => {
            setSettingsDialogOpen(false)
            setSelectedTransfer(null)
          }}
          shareLink={selectedTransfer.shareLink || ""}
          shareCode={selectedTransfer.shareKey || ""}
          description={selectedTransfer.description}
          encrypted={selectedTransfer.encrypted}
          allowDownload={selectedTransfer.allowDownload}
          allowClone={selectedTransfer.allowClone}
          allowTransfer={selectedTransfer.allowTransfer}
          burnAfterRead={selectedTransfer.burnAfterRead}
          maxDownloads={selectedTransfer.maxDownloads}
          expireAt={selectedTransfer.expiresAt}
        />
      )}
    </div>
  )
}
